<template>
  <AppHeaderLogo
    :to="localePath({ name: 'index' })"
    class="self-center"
    :class="{ 'max-xl:hidden': hasFocusOnSearchForm }"
  />

  <template v-if="isHeaderSearchFormInHeader">
    <AtomTransition
      timing="fast"
      :is-disabled="!isRenderedHeaderSearchFormVisibilityDynamic"
    >
      <LazySearchFormBar
        v-show="isHeaderSearchFormVisible"
        class="grow mx-auto justify-items-center"
        is-toggleable
        :has-rental-type="!isRenderedRvSearchPage"
        :is-campgrounds="isRenderedCampgroundSearchPage"
        @search-form-visibility-change="handleSearchFormVisibilityChange"
      />
    </AtomTransition>
  </template>

  <template v-if="!route.meta?.hideNavigation">
    <div
      class="shrink-0 flex items-center gap-x-4"
      :class="{ 'max-xl:hidden': hasFocusOnSearchForm }"
    >
      <div class="hidden lg:block">
        <template v-if="userExists">
          <NuxtLink
            v-if="userHasRv"
            :to="localePath({ name: 'dashboard', params: { dashboard: 'host' } })"
            :class="computedHostModeButtonClasses"
           
          >
            {{ t('switchToHosting') }}
          </NuxtLink>

          <NuxtLink
            v-else
            :to="localePath({ name: 'dashboard-rvs-new', params: { dashboard: 'host' } })"
            :class="computedHostModeButtonClasses"
           
          >
            {{ t('pageLink.listAnRV') }}
          </NuxtLink>
        </template>

        <NuxtLink
          v-else
          :to="localePath({ name: 'owner' })"
          :external="isRouteOwnerPage"
          :class="computedHostModeButtonClasses"
          @click="handleOwnerSignup($event, isRouteOwnerPage)"
        >
          {{ t('pageLink.listAnRV') }}
        </NuxtLink>
      </div>

      <NuxtLink
        v-if="userExists"
        :to="localePath({ name: 'profile-id', params: { id: userId }, query: { cta: 'header-nav-avatar' } })"
       
        class="leading-none"
      >
        <TAvatar
          :src="userAvatar"
          :text="userExists ? userInitials : undefined"
          size="lg"
        />
      </NuxtLink>

      <TMenu
        :show-badge="showBadge"
       
      >
        <template v-if="userExists">
          <div class="py-1">
            <TMenuItem
              :to="localePath({ name: 'dashboard', params: { dashboard: dashboardMode } })"
              :text="t('pageLink.dashboard')"
             
              weight="medium"
              :badge-amount="dashboardNotificationsTotal"
            >
              <template #icon>
                <TIconCustom
                  name="favicon"
                  fixed-width
                />
              </template>
            </TMenuItem>

            <TMenuItem
              :to="localePath({ name: 'dashboard-messages', params: { dashboard: dashboardMode } })"
              :text="t('pageLink.inbox')"
              icon="comment"
             
              weight="medium"
              :badge-amount="inboxNotificationsTotal"
            />

            <TMenuItem
              :to="localePath({ name: 'dashboard-trips', params: { dashboard: dashboardMode } })"
              :text="t('pageLink.trips')"
              icon="circle-location-arrow"
              weight="medium"
            />

            <TMenuItem
              :to="localePath({ name: 'dashboard-favourites', params: { dashboard: dashboardMode } })"
              :text="t('pageLink.favourites')"
              icon="heart"
              weight="medium"
            />
          </div>

          <div class="py-1">
            <AppHeaderMenuListAnRv />
          </div>

          <div class="py-1">
            <TMenuItem
              :to="localePath({ name: 'dashboard-profile', params: { dashboard: dashboardMode } })"
              :text="t('pageLink.account')"
              icon="gear"
              :show-badge="!userIsVerified || !userPhoneNumber"
            />

            <TMenuItem
              :to="t('helpCenter.url')"
              :text="t('helpCenter.title')"
              icon="message-question"
              external
              @click.prevent="handleTrackHelpCenterClicked"
            />

            <TMenuItem
              v-if="userHasRv"
              :to="localePath({ name: 'dashboard', params: { dashboard: 'host' } })"
              :text="t('switchToHosting')"
              icon="rotate"
              class="lg:hidden"
            />
          </div>

          <div class="py-1">
            <TMenuItem
              :text="t('actions.logOut')"
             
              @click.prevent="logOut()"
            />
          </div>

          <div
            v-if="userIsAdmin"
            class="py-1"
          >
            <div class="px-4 mt-2 text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1">
              Admin
            </div>

            <TMenuItem
              :to="runtimeConfig.public.apps.operationsDashboard.baseURL"
              external
              text="Operations"
            />
          </div>
        </template>

        <template v-else>
          <div class="py-1">
            <AppHeaderMenuListAnRv @click="handleOwnerSignup($event, isRouteOwnerPage)" />
          </div>

          <div class="py-1">
            <TMenuItem
              :to="t('helpCenter.url')"
              :text="t('helpCenter.title')"
              icon="message-question"
              external
              @click="handleTrackHelpCenterClicked"
            />
          </div>

          <div class="py-1">
            <TMenuItem
              :to="localePath('index-signup')"
              external
              :text="t('signUp')"
             
              @click="handleOwnerSignup($event, true)"
            />

            <TMenuItem
              :to="localePath('index-signin')"
              external
              :text="t('login')"
             
              @click.prevent="showLogin"
            />
          </div>
        </template>
      </TMenu>
    </div>
  </template>
</template>

<script setup lang="ts">
const route = useRoute()
const { dashboardMode, isRouteOwnerPage, isRenderedRvSearchPage, isRenderedCampgroundSearchPage } = useBaseName()
const localePath = useLocalePath()
const { t } = useI18n()
const runtimeConfig = useRuntimeConfig()
const {
  dashboardNotificationsTotal,
  inboxNotificationsTotal,
} = useNotificationsData()

const { logOut } = useAuthentication()
const {
  userInitials,
  userAvatar,
  userId,
  userHasRv,
  userPhoneNumber,
  userExists,
  userIsAdmin,
  userIsVerified,
} = useUser()

const { showLogin } = useLogin()
const { showSignup } = useSignup()

const { hydrationStatus } = useHydrationState()
const showBadge = computed(() => {
  if (!hydrationStatus.value) {
    return false
  }

  return dashboardNotificationsTotal.value > 0 || inboxNotificationsTotal.value > 0 || (userExists.value && (!userIsVerified.value || !userPhoneNumber.value))
})

const { pageSource } = usePageSource()
function handleTrackHelpCenterClicked() {
  trackHelpCenterClicked({
    pageSource: pageSource.value,
  })
}

function handleOwnerSignup(e: Event, preventDefault = false) {
  if (preventDefault) {
    e.preventDefault()

    showSignup({ intent: isRouteOwnerPage.value ? 'ListingIntent' : undefined })
  }
}

const { isHeaderSearchFormInHeader, isHeaderSearchFormVisible, isRenderedHeaderSearchFormVisibilityDynamic } = useHeaderSearchFormVisibility()

const computedHostModeButtonClasses = computed(() => {
  return 'text-sm/6 font-semibold text-primary-950 rounded-full py-2.5 px-4 whitespace-nowrap transition-colors duration-300 ease-in-out hover:bg-gray-50'
})

const hasFocusOnSearchForm = ref(false)

function handleSearchFormVisibilityChange(value: boolean) {
  hasFocusOnSearchForm.value = value
}
</script>

<i18n src="~~/i18n/locales/common/app.json" lang="json" />
